import { socialExport } from '@mi-front/api-clients';
import { Stack } from '@mui/material';
import { useMemo, useState } from 'react';
import { YoutubePlaylistsAutocomplete } from './YoutubePlaylistsAutocomplete';
import { YoutubePlaylistsNoPermissionsAlert } from './YoutubePlaylistsNoPermissionsAlert';
import { YoutubePlaylistsRefreshLink } from './YoutubePlaylistsRefreshLink';

type Props = {
  stationId: string;
};

/**
 * Multi-select field for YouTube playlists.
 * Fetches playlists from the API and allows selecting multiple playlists.
 * Includes a clickable link in the helper text to refresh playlists from YouTube.
 * Disabled when the station's YouTube OAuth credentials lack playlist management scope.
 */
const MAX_PLAYLISTS = 10;

export function YoutubePlaylistsField({ stationId }: Props) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data: youtubeCapabilities, isLoading: isLoadingYoutubeCapabilities } =
    socialExport.useGetYoutubeCapabilities(stationId);
  const canManagePlaylists =
    !isLoadingYoutubeCapabilities && (youtubeCapabilities?.canManagePlaylists ?? false);
  const showNoPermissionsMessage = !isLoadingYoutubeCapabilities && !canManagePlaylists;

  const playlistsQuery = socialExport.useGetYoutubePlaylists(stationId, {
    query: {
      enabled: !!stationId && canManagePlaylists,
    },
  });

  const playlists = playlistsQuery.data?.data ?? [];
  const isLoading = playlistsQuery.isLoading || isRefreshing;

  // Sort playlists by title for better UX
  const sortedPlaylists = useMemo(() => {
    return [...playlists].sort((a, b) => a.title.localeCompare(b.title));
  }, [playlists]);

  const helperText = (
    <YoutubePlaylistsRefreshLink
      stationId={stationId}
      isLoading={isLoading}
      onRefreshStateChange={setIsRefreshing}
    />
  );

  const fieldHelperText = canManagePlaylists ? helperText : undefined;

  return (
    <Stack spacing={1}>
      <YoutubePlaylistsAutocomplete
        playlists={sortedPlaylists}
        isLoading={isLoading}
        maxSelected={MAX_PLAYLISTS}
        helperText={fieldHelperText}
        disabled={!canManagePlaylists}
      />
      {showNoPermissionsMessage && <YoutubePlaylistsNoPermissionsAlert />}
    </Stack>
  );
}
